<template>
  <MModal
    class="table-settings-modal-container"
  >
    <template #modal-header>
      <div class="table-settings-modal__header">
        <template v-if="isEditing">
          {{ _getTr("edit_table_preset") }}
        </template>
        <template v-else>
          {{ _getTr("table_settings") }}
        </template>
      </div>
      <div class="table-settings-modal__reset">
        <MLink
          v-if="defaultColumns.length > 0"
          class="table-settings-reset__reset-icon"
          :icon="undoIcon"
          @click="setDefaultPreset"
        >
          {{ _getTr("default_settings") }}
        </MLink>
      </div>
    </template>
    <template #modal-content>
      <div class="table-settings-modal">
        <div class="table-settings-modal__items">
          <ColumnsSelect
            :value="innerValue"
            :available-columns="availableColumns"
            @toggleItems="toggleItems"
          />
        </div>
        <div class="table-settings-modal__order">
          <ColumnsOrder
            :columns-map="columnsMap"
            :selected-items="innerValue"
            @reorder="onItemsReorder"
            @toggleItems="toggleItems"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <TableSettingsModalSave
        :columns="innerValue"
        :preset-id="presetId"
        :preset-data="presetData"
        :preset-save-form-component="presetSaveFormComponent"
        :preset-save-form-payload="presetSaveFormPayload"
        :preset-save-form-handlers="presetSaveFormHandlers"
        @apply="applyPreset"
        @cancel="handleCancel"
        @saveAndApplyPreset="saveAndApplyPreset"
      />
    </template>
  </MModal>
</template>

<script>
  import MModal from 'components/Modals/MModal/MModal';
  import ColumnsSelect from 'components/Data/TableSettings/TableSettingsModal/ColumnsSelect/ColumnsSelect';
  import ColumnsOrder from 'components/Data/TableSettings/TableSettingsModal/ColumnsOrder/ColumnsOrder';
  import MLink from 'components/Links/MLink';
  import undoIcon from 'assets/icons/undo.svg';
  import TableSettingsModalSave from 'components/Data/TableSettings/TableSettingsModal/TableSettingsModalSave';
  import _ from 'lodash';
  import addItemsToArrayPreserveSorting from 'utils/array/addItemsToArrayPreserveSorting';

  _.noConflict();

  export default {
    name: 'TableSettingsModal',
    components: {TableSettingsModalSave, ColumnsOrder, ColumnsSelect, MModal, MLink},
    props: {
      value: {
        type: Array,
        default: Array
      },
      presetId: {
        type: Number,
        default: null
      },
      presetData: {
        type: Object,
        default: Object
      },
      isEditing: {
        type: Boolean,
        default: false
      },
      availableColumns: {
        type: Array,
        required: true
      },
      availableColumnsFlat: {
        type: Array,
        default: Array
      },
      defaultColumns: {
        type: Array,
        default: Array
      },
      preserveSorting: {
        type: Boolean,
        default: false
      },
      presetSaveFormComponent: {
        type: Object,
        default: null
      },
      presetSaveFormPayload: {
        type: Object,
        default: null
      },
      presetSaveFormHandlers: {
        type: Object,
        default: Object
      }
    },
    data () {
      return {
        undoIcon,

        innerValue: []
      };
    },
    computed: {
      columnsMap () {
        return _.keyBy(this.availableColumnsFlat, 'id');
      },
      orderedColumnIdsFlat () {
        return this.availableColumnsFlat.map(c => c.id);
      }
    },
    watch: {
      value: {
        immediate: true,
        handler () {
          this.innerValue = [...this.value];
        }
      }
    },
    methods: {
      setDefaultPreset () {
        this.innerValue = [...this.defaultColumns];
      },
      toggleItems ({status, itemIds}) {
        if (status) {
          let newVal;
          if (this.preserveSorting) {
            newVal = addItemsToArrayPreserveSorting(
              [...this.innerValue],
              itemIds,
              this.orderedColumnIdsFlat
            );
          } else {
            newVal = Array.from(new Set([...this.innerValue, ...itemIds]));
          }

          // merge union-group values
          newVal = Object.values(_.groupBy(
            newVal,
            (item) => {
              const i = this.columnsMap[item];
              return i.groupInfo.union ? i.groupInfo.id : i.id;
            })).flat();

          this.innerValue = _.uniq(newVal, 'id');
        } else {
          const toggleSet = new Set(itemIds);
          this.innerValue = this.innerValue.filter(i => !toggleSet.has(i));
        }
      },
      onItemsReorder (newOrder) {
        this.innerValue = newOrder;
      },
      applyPreset () {
        this.$emit('applyPreset', {columns: this.innerValue});
      },
      handleCancel () {
        this.$emit('cancel');
      },
      saveAndApplyPreset (presetId) {
        this.$emit('saveAndApplyPreset', presetId);
      }
    }
  };
</script>

<style>
  .table-settings-modal-container .m-modal__header {
    display: flex;
    align-items: center;
  }

  .table-settings-modal {
    display: flex;
    height: 450px;
  }

  .table-settings-modal__items,
  .table-settings-modal__order {
    overflow-y: auto;

    flex-grow: 0;
    flex-shrink: 0;

    min-height: 100%;
  }

  .table-settings-modal__items {
    width: 402px;
  }

  .table-settings-modal__order {
    width: 266px;
    background: var(--gray-table-color);
  }

  .table-settings-modal__reset {
    font-size: 14px;
    font-weight: var(--medium-font-weight);

    margin-left: auto;
  }
</style>
